import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Plus, Trash2, GripVertical, Save, Sparkles, ListPlus } from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { useBillResources } from "@/hooks/useBillResources";
import { useSupplier } from "@/hooks/useSupplier";
import { CreateSupplierDialog } from "@/components/CreateSupplierDialog";
import { BillDocumentViewer } from "@/components/BillDocumentViewer";
import { CategoryCombobox } from "@/components/CategoryCombobox";
import { TagInput } from "@/components/TagInput";
import { DatePicker } from "@/components/ui/date-picker";
import { numberFieldProps } from "@/lib/number-field";
import { InfoHint } from "@/components/InfoHint";
import { EmptyState, ErrorState, LoadingState } from "@/components/PageUi";
import type { components } from "@dolfin/api-public";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Spinner } from "@/components/ui/spinner";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import { formatCurrency, parseUnitOfMeasure, unitOfMeasureAbbr } from "@/lib/format";
import { UnitCombobox } from "@/components/UnitCombobox";

type BillResponse = components["schemas"]["BillResponse"];
type SpendClass = "Fixed" | "Variable";

const SPEND_CLASS_OPTIONS: { value: SpendClass; label: string }[] = [
  { value: "Fixed", label: "Fixed" },
  { value: "Variable", label: "Variable" },
];

/** Read-only suggestion metadata the agent attaches to a line. */
interface LineSuggestion {
  suggestedCategory?: string | null;
  categoryConfidence?: number | null;
  suggestedSpendClass?: SpendClass | null;
  spendClassConfidence?: number | null;
}

const editBillSchema = z.object({
  supplierId: z.string().nullable(),
  supplierName: z.string().nullable(),
  supplierEmail: z.string().nullable(),
  supplierPhone: z.string().nullable(),
  supplierAddress: z.string().nullable(),
  deliveryAddress: z.string().nullable(),
  supplierTaxId: z.string().nullable(),
  supplierWebsite: z.string().nullable(),
  supplierRegistrationNumber: z.string().nullable(),
  invoiceNumber: z.string().nullable(),
  // The references as one comma-separated line — what the reviewer types. Parsed into the list the
  // API takes on submit; see parsePoNumbers.
  billPoNumbers: z.string(),
  invoiceDate: z.string().nullable(),
  dueDate: z.string().nullable(),
  currency: z.string().min(1, "Currency is required"),
  subTotal: z.number(),
  taxAmount: z.number(),
  totalAmount: z.number(),
  paymentTerms: z.string().nullable(),
  paymentRef: z.string().nullable(),
  accountNumber: z.string().nullable(),
  sortCode: z.string().nullable(),
  iban: z.string().nullable(),
  lineItems: z.array(
    z.object({
      id: z.string().nullable(),
      description: z.string().min(1, "Description is required"),
      sku: z.string().nullable(),
      quantity: z.number(),
      unit: z.string().nullable(),
      unitPrice: z.number(),
      // taxAmount is the authoritative tax figure persisted as-is; taxRate is descriptive only.
      taxAmount: z.number(),
      taxRate: z.number(),
      sortOrder: z.number(),
      category: z.string().nullable(),
      spendClass: z.enum(["Fixed", "Variable"]).nullable(),
      tags: z.array(z.string()),
    }),
  ),
});

type EditBillFormValues = z.infer<typeof editBillSchema>;

const DIRTY_RING = "ring-2 ring-amber-500/50";

function parseNumber(value: number | string | null | undefined): number {
  if (value == null) return 0;
  return typeof value === "string" ? parseFloat(value) || 0 : value;
}

// Bills print several POs as a run ("PO-A/ PO-B"), so split on comma and slash. Empty parses to [], which the
// API reads as "quotes no order", not "leave alone".
function parsePoNumbers(value: string): string[] {
  return value
    .split(/[,/]/)
    .map((poNumber) => poNumber.trim())
    .filter((poNumber) => poNumber.length > 0);
}

function numberOrNull(value: number | string | null | undefined): number | null {
  if (value == null) return null;
  const n = typeof value === "string" ? parseFloat(value) : value;
  return Number.isNaN(n) ? null : n;
}

function toDateInputValue(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  return dateStr.substring(0, 10);
}

function confidenceLabel(confidence: number | string | null | undefined): string | null {
  if (confidence == null) return null;
  return `${Math.round(Number(confidence) * 100)}%`;
}

function LineItemRow({
  index,
  control,
  categories,
  suggestion,
  dragIndex,
  lineTotal,
  currency,
  onDragStart,
  onDragOver,
  onDragEnd,
  onRemove,
}: {
  index: number;
  lineTotal: number;
  currency: string;
  control: ReturnType<typeof useForm<EditBillFormValues>>["control"];
  categories: components["schemas"]["ResolvedCategoryResponse"][];
  suggestion: LineSuggestion | undefined;
  dragIndex: number | null;
  onDragStart: (index: number) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDragEnd: () => void;
  onRemove: (index: number) => void;
}) {
  const categoryName = (key: string) =>
    categories.find((c) => c.key === key)?.displayName ?? key;
  return (
    <div
      draggable
      onDragStart={() => onDragStart(index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDragEnd={onDragEnd}
      className={cn(
        "space-y-3 rounded-xl border bg-background/60 p-4 transition-opacity",
        dragIndex === index && "opacity-50",
      )}
    >
      <div className="flex items-center gap-2">
        <GripVertical className="size-4 shrink-0 cursor-grab text-muted-foreground" />
        <span className="grid size-6 place-items-center rounded-md border bg-muted/50 text-[11px] font-semibold tabular-nums text-muted-foreground">
          {index + 1}
        </span>
        <div className="flex-1" />
        {/* The line's gross as it will post, so a mistyped quantity or price shows up before saving. */}
        <span className="text-sm font-semibold tabular-nums">{formatCurrency(lineTotal, currency)}</span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onRemove(index)}
          aria-label={`Remove item ${index + 1}`}
          className="text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="size-4" />
        </Button>
      </div>

      <FormField
        control={control}
        name={`lineItems.${index}.description`}
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel className="text-xs">Description</FormLabel>
            <FormControl>
              <Input
                {...field}
                placeholder="Line item description"
                className={cn(fieldState.isDirty && DIRTY_RING)}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={`lineItems.${index}.sku`}
        render={({ field, fieldState }) => (
          <FormItem>
            <div className="flex items-center gap-1">
              <FormLabel className="text-xs">SKU</FormLabel>
              <InfoHint label="SKU">
                Product code used as the exact-match key against purchase-order and delivery-note lines in
                three-way matching.
              </InfoHint>
            </div>
            <FormControl>
              <Input
                {...field}
                value={field.value ?? ""}
                onChange={(e) => field.onChange(e.target.value || null)}
                placeholder="Optional product code"
                className={cn(fieldState.isDirty && DIRTY_RING)}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid grid-cols-3 gap-3">
        <FormField
          control={control}
          name={`lineItems.${index}.quantity`}
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel className="text-xs">Quantity</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="any"
                  {...field}
                  {...numberFieldProps(field)}
                  className={cn(fieldState.isDirty && DIRTY_RING)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={`lineItems.${index}.unit`}
          render={({ field, fieldState }) => {
            const uom = parseUnitOfMeasure(field.value);
            return (
              <FormItem>
                <div className="flex items-center gap-1">
                  <FormLabel className="text-xs">Unit</FormLabel>
                  {uom && (
                    <InfoHint label="Unit">
                      Structured as {unitOfMeasureAbbr(uom)}, so weighed goods can be matched by weight.
                    </InfoHint>
                  )}
                </div>
                <FormControl>
                  <UnitCombobox
                    value={field.value}
                    onChange={field.onChange}
                    className={cn(fieldState.isDirty && DIRTY_RING)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <FormField
          control={control}
          name={`lineItems.${index}.unitPrice`}
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel className="text-xs">Unit Price</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  {...field}
                  {...numberFieldProps(field)}
                  className={cn(fieldState.isDirty && DIRTY_RING)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <FormField
          control={control}
          name={`lineItems.${index}.taxAmount`}
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel className="text-xs">Tax Amount</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  {...field}
                  {...numberFieldProps(field)}
                  className={cn(fieldState.isDirty && DIRTY_RING)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={`lineItems.${index}.taxRate`}
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel className="text-xs">Tax Rate (%)</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  {...field}
                  {...numberFieldProps(field)}
                  className={cn(fieldState.isDirty && DIRTY_RING)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 border-t border-border/60 pt-3">
        <FormField
          control={control}
          name={`lineItems.${index}.category`}
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel className="text-xs">Category</FormLabel>
              <FormControl>
                <CategoryCombobox
                  value={field.value}
                  onChange={field.onChange}
                  categories={categories}
                  className={cn(fieldState.isDirty && DIRTY_RING)}
                />
              </FormControl>
              {suggestion?.suggestedCategory &&
                field.value !== suggestion.suggestedCategory && (
                  <button
                    type="button"
                    onClick={() => field.onChange(suggestion.suggestedCategory!)}
                    className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground transition hover:text-foreground"
                  >
                    <Sparkles className="size-3 text-accent-blue" />
                    Suggested: {categoryName(suggestion.suggestedCategory)}
                    {confidenceLabel(suggestion.categoryConfidence) && (
                      <span className="opacity-70">
                        · {confidenceLabel(suggestion.categoryConfidence)}
                      </span>
                    )}
                  </button>
                )}
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={`lineItems.${index}.spendClass`}
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel className="text-xs">Spend class</FormLabel>
              <Select
                value={field.value ?? "__none__"}
                onValueChange={(val) =>
                  field.onChange(val === "__none__" ? null : (val as SpendClass))
                }
              >
                <FormControl>
                  <SelectTrigger
                    className={cn("w-full", fieldState.isDirty && DIRTY_RING)}
                  >
                    <SelectValue placeholder="Unset" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="__none__">Unset</SelectItem>
                  {SPEND_CLASS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {suggestion?.suggestedSpendClass &&
                field.value !== suggestion.suggestedSpendClass && (
                  <button
                    type="button"
                    onClick={() => field.onChange(suggestion.suggestedSpendClass!)}
                    className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground transition hover:text-foreground"
                  >
                    <Sparkles className="size-3 text-accent-blue" />
                    Suggested: {suggestion.suggestedSpendClass}
                    {confidenceLabel(suggestion.spendClassConfidence) && (
                      <span className="opacity-70">
                        · {confidenceLabel(suggestion.spendClassConfidence)}
                      </span>
                    )}
                  </button>
                )}
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={control}
        name={`lineItems.${index}.tags`}
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel className="text-xs">Tags</FormLabel>
            <FormControl>
              <TagInput
                value={field.value}
                onChange={field.onChange}
                className={cn(fieldState.isDirty && DIRTY_RING)}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

export interface EditBillPageProps {
  billId: string;
  onSaved?: (billId: string) => void;
  onCancel?: () => void;
  onSupplierClick?: (supplierId: string) => void;
}

export function EditBillPage({ billId, onSaved, onCancel, onSupplierClick }: EditBillPageProps) {
  const api = useDolfinClient();
  const resources = useBillResources();
  const { suppliers, categories } = resources;
  const [bill, setBill] = useState<BillResponse | null>(null);
  const { supplier: matchedSupplier } = useSupplier(bill?.suggestedMatchedSupplierId);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  // Bumped by "Try again" on the load error to re-run the fetch.
  const [reloadKey, setReloadKey] = useState(0);
  const dragIndexRef = useRef<number | null>(null);

  const form = useForm<EditBillFormValues>({
    resolver: zodResolver(editBillSchema),
    defaultValues: {
      supplierId: null,
      supplierName: null,
      supplierEmail: null,
      supplierPhone: null,
      supplierAddress: null,
      deliveryAddress: null,
      supplierTaxId: null,
      supplierWebsite: null,
      supplierRegistrationNumber: null,
      invoiceNumber: null,
      billPoNumbers: "",
      invoiceDate: null,
      dueDate: null,
      currency: "GBP",
      subTotal: 0,
      taxAmount: 0,
      totalAmount: 0,
      paymentTerms: null,
      paymentRef: null,
      accountNumber: null,
      sortCode: null,
      iban: null,
      lineItems: [],
    },
  });

  // keyName "_rhfId" frees the default "id" key so `field.id` carries our own
  // line id (used to look up the agent's per-line suggestions).
  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: "lineItems",
    keyName: "_rhfId",
  });

  // Read-only suggestion metadata keyed by line id, sourced from the bill (not
  // the form, which only holds editable fields). Survives drag reordering.
  const suggestionsById = useMemo(() => {
    const map = new Map<string, LineSuggestion>();
    for (const li of bill?.lineItems ?? []) {
      if (li.id) {
        map.set(li.id, {
          suggestedCategory: li.suggestedCategory,
          categoryConfidence: numberOrNull(li.categoryConfidence),
          suggestedSpendClass: li.suggestedSpendClass ?? null,
          spendClassConfidence: numberOrNull(li.spendClassConfidence),
        });
      }
    }
    return map;
  }, [bill]);

  useEffect(() => {
    if (!billId) return;

    async function fetchData() {
      setLoading(true);
      setError(null);

      try {
        const [billRes] = await Promise.all([
          api.GET("/v1/bills/{id}", { params: { path: { id: billId } } }),
          resources.fetchAll(),
        ]);

        if (billRes.error || !billRes.data) {
          setError("Failed to load bill");
          return;
        }

        if (billRes.data.state !== "PendingReview") {
          setError("Only bills in Pending Review state can be edited");
          return;
        }

        const b = billRes.data;
        setBill(b);

        form.reset({
          supplierId: b.supplierId ?? null,
          supplierName: b.supplierName ?? null,
          supplierEmail: b.supplierEmail ?? null,
          supplierPhone: b.supplierPhone ?? null,
          supplierAddress: b.supplierAddress ?? null,
          deliveryAddress: b.deliveryAddress ?? null,
          supplierTaxId: b.supplierTaxId ?? null,
          supplierWebsite: b.supplierWebsite ?? null,
          supplierRegistrationNumber: b.supplierRegistrationNumber ?? null,
          invoiceNumber: b.invoiceNumber ?? null,
          billPoNumbers: (b.billPoNumbers ?? []).join(", "),
          invoiceDate: toDateInputValue(b.invoiceDate),
          dueDate: toDateInputValue(b.dueDate),
          currency: b.currency ?? "GBP",
          subTotal: parseNumber(b.subTotal),
          taxAmount: parseNumber(b.taxAmount),
          totalAmount: parseNumber(b.totalAmount),
          paymentTerms: b.paymentTerms ?? null,
          paymentRef: b.paymentRef ?? null,
          accountNumber: b.bankDetails?.accountNumber ?? null,
          sortCode: b.bankDetails?.sortCode ?? null,
          iban: b.bankDetails?.iban ?? null,
          lineItems:
            b.lineItems?.map((item, idx) => ({
              id: item.id ?? null,
              description: item.description ?? "",
              sku: item.sku ?? null,
              quantity: parseNumber(item.quantity),
              unit: item.unit ?? null,
              unitPrice: parseNumber(item.unitPrice),
              taxAmount: parseNumber(item.taxAmount),
              taxRate: parseNumber(item.taxRate),
              sortOrder: parseNumber(item.sortOrder) || idx,
              // Seed from confirmed fields only; suggestion-only lines stay unset until the reviewer accepts the chip
              // (or submit-review promotes it server-side).
              category: item.category ?? null,
              spendClass: item.spendClass ?? null,
              tags: item.tags ?? [],
            })) ?? [],
        });
      } catch (e) {
        console.error("Failed to load data:", e);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billId, reloadKey]);

  async function onSubmit(values: EditBillFormValues) {
    if (!billId) return;
    setSaving(true);
    setSaveError(null);

    try {
      const { error: apiError } = await api.PATCH("/v1/bills/{id}", {
        params: { path: { id: billId } },
        body: {
          supplierId: values.supplierId || null,
          supplierName: values.supplierName || null,
          supplierEmail: values.supplierEmail || null,
          supplierPhone: values.supplierPhone || null,
          supplierAddress: values.supplierAddress || null,
          deliveryAddress: values.deliveryAddress || null,
          supplierTaxId: values.supplierTaxId || null,
          supplierWebsite: values.supplierWebsite || null,
          supplierRegistrationNumber: values.supplierRegistrationNumber || null,
          invoiceNumber: values.invoiceNumber || null,
          billPoNumbers: parsePoNumbers(values.billPoNumbers),
          invoiceDate: values.invoiceDate || null,
          dueDate: values.dueDate || null,
          currency: values.currency || null,
          subTotal: values.subTotal,
          taxAmount: values.taxAmount,
          totalAmount: values.totalAmount,
          paymentTerms: values.paymentTerms || null,
          paymentRef: values.paymentRef || null,
          accountNumber: values.accountNumber || null,
          sortCode: values.sortCode || null,
          iban: values.iban || null,
          lineItems: values.lineItems.map((item, idx) => ({
            id: item.id || undefined,
            description: item.description,
            // Null leaves the line's existing SKU untouched; a value overwrites it.
            sku: item.sku || null,
            quantity: item.quantity,
            unit: item.unit || null,
            unitPrice: item.unitPrice,
            taxAmount: item.taxAmount,
            taxRate: item.taxRate,
            sortOrder: idx,
            // Merge-by-id: non-null confirms (and clears the suggestion), null leaves it. Seeded from confirmed values,
            // so resending never wipes a category.
            category: item.category || null,
            spendClass: item.spendClass ?? null,
            tags: item.tags,
          })),
        },
      });

      if (apiError) {
        const message = apiErrorMessage(apiError, "Failed to save bill.");
        setSaveError(message);
        toast.error(message);
        return;
      }

      onSaved?.(billId);
    } catch (e) {
      console.error("Failed to save bill:", e);
      setSaveError("An unexpected error occurred while saving.");
    } finally {
      setSaving(false);
    }
  }

  function handleDragStart(index: number) {
    dragIndexRef.current = index;
    setDragIndex(index);
  }

  function handleDragOver(e: React.DragEvent, index: number) {
    e.preventDefault();
    if (dragIndexRef.current === null || dragIndexRef.current === index) return;
    move(dragIndexRef.current, index);
    dragIndexRef.current = index;
  }

  function handleDragEnd() {
    dragIndexRef.current = null;
    setDragIndex(null);
  }

  const watchedLines = form.watch("lineItems");
  const watchedTotal = form.watch("totalAmount");
  const watchedCurrency = form.watch("currency");

  const goBack = () => (onCancel ? onCancel() : window.history.back());

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-2xl p-6">
        <LoadingState label="Loading bill..." />
      </div>
    );
  }

  if (error || !bill) {
    return (
      <div className="mx-auto w-full max-w-2xl space-y-4 p-6">
        <ErrorState message={error || "Bill not found"} onRetry={() => setReloadKey((k) => k + 1)} />
        <div className="flex justify-center">
          <Button variant="outline" onClick={goBack}>
            <ArrowLeft className="size-4" />
            Back to bill
          </Button>
        </div>
      </div>
    );
  }

  const supplierLabel = bill.supplierName || matchedSupplier?.name || "Unknown supplier";
  const currency = watchedCurrency || bill.currency || "GBP";
  const lineTotals = (watchedLines ?? []).map(
    (l) => parseNumber(l?.quantity) * parseNumber(l?.unitPrice) + parseNumber(l?.taxAmount),
  );
  const linesTotal = lineTotals.reduce((sum, t) => sum + t, 0);
  const linesMismatch = Math.abs(linesTotal - parseNumber(watchedTotal)) > 0.005;

  return (
    <div className="h-full flex flex-col">
      <div className="flex shrink-0 items-center justify-between gap-4 border-b px-6 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition hover:text-foreground"
          >
            <ArrowLeft className="size-3.5" />
            Back
          </button>
          <span className="text-muted-foreground">/</span>
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Edit bill
            </p>
            <h1 className="truncate text-sm font-bold tracking-tight">
              {supplierLabel}
              {bill.invoiceNumber && (
                <span className="ml-2 font-mono text-xs font-normal text-muted-foreground">
                  {bill.invoiceNumber}
                </span>
              )}
            </h1>
          </div>
        </div>
      </div>

      <ResizablePanelGroup orientation="horizontal" className="flex-1">
        <ResizablePanel defaultSize={50} minSize={25}>
          <div className="h-full bg-muted/30">{billId && <BillDocumentViewer billId={billId} />}</div>
        </ResizablePanel>

        <ResizableHandle withHandle />

        <ResizablePanel defaultSize={50} minSize={25}>
          <div className="h-full overflow-auto px-6 pt-6">
            <div className="max-w-2xl mx-auto">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <Card className="bg-card/60 backdrop-blur-sm">
                    <CardHeader>
                      <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        Bill details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="supplierId"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel>Supplier</FormLabel>
                              <Select
                                onValueChange={(val) => {
                                  if (val === "__new_supplier__") {
                                    resources.setNewSupplierOpen(true);
                                    return;
                                  }
                                  field.onChange(val === "__none__" ? null : val);
                                }}
                                value={field.value ?? "__none__"}
                              >
                                <FormControl>
                                  <SelectTrigger className={cn("w-full", fieldState.isDirty && DIRTY_RING)}>
                                    <SelectValue placeholder="Select supplier" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="__none__">No supplier</SelectItem>
                                  {suppliers.map((supplier) => (
                                    <SelectItem key={supplier.id} value={supplier.id!}>
                                      {supplier.name}
                                    </SelectItem>
                                  ))}
                                  <SelectItem value="__new_supplier__" className="text-primary font-medium">
                                    <Plus className="size-3 inline mr-1" />
                                    New Supplier
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <div className="flex flex-col justify-end gap-1.5 pb-0.5">
                          <p className="text-xs text-muted-foreground">
                            A supplier will be created if one is not provided.
                          </p>
                          {matchedSupplier && bill.suggestedMatchedSupplierId && (
                            <div className="flex items-center gap-2">
                              <p className="text-xs text-muted-foreground">
                                Suggested:{" "}
                                {onSupplierClick ? (
                                  <button
                                    type="button"
                                    onClick={() => onSupplierClick(bill.suggestedMatchedSupplierId!)}
                                    className="font-medium hover:underline cursor-pointer"
                                  >
                                    {matchedSupplier.name}
                                  </button>
                                ) : (
                                  <span className="font-medium">{matchedSupplier.name}</span>
                                )}
                                {confidenceLabel(bill.suggestedMatchedSupplierConfidence) && (
                                  <span className="ml-1 text-muted-foreground">
                                    ({confidenceLabel(bill.suggestedMatchedSupplierConfidence)})
                                  </span>
                                )}
                              </p>
                              {form.watch("supplierId") !== bill.suggestedMatchedSupplierId && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-6 text-xs px-2"
                                  onClick={() =>
                                    form.setValue("supplierId", bill.suggestedMatchedSupplierId!, {
                                      shouldDirty: true,
                                    })
                                  }
                                >
                                  Use
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <FormField
                          control={form.control}
                          name="currency"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel>Currency</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="e.g. GBP"
                                  className={cn(fieldState.isDirty && DIRTY_RING)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="invoiceNumber"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel>Invoice Number</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => field.onChange(e.target.value || null)}
                                  placeholder="e.g. INV-001"
                                  className={cn(fieldState.isDirty && DIRTY_RING)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="billPoNumbers"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel>Extracted PO Numbers</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => field.onChange(e.target.value)}
                                  placeholder="e.g. PO-2026-0042, PO-2026-0043"
                                  className={cn(fieldState.isDirty && DIRTY_RING)}
                                />
                              </FormControl>
                              <FormDescription>
                                Separate several orders with a comma. Clear the field if the bill quotes none.
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="paymentTerms"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel>Payment Terms</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => field.onChange(e.target.value || null)}
                                  placeholder="e.g. Net 30"
                                  className={cn(fieldState.isDirty && DIRTY_RING)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="invoiceDate"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel>Invoice Date</FormLabel>
                              <DatePicker
                                value={field.value}
                                onChange={(v) => field.onChange(v)}
                                placeholder="Invoice date"
                                className={cn(fieldState.isDirty && DIRTY_RING)}
                              />
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="dueDate"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel>Due Date</FormLabel>
                              <DatePicker
                                value={field.value}
                                onChange={(v) => field.onChange(v)}
                                placeholder="Due date"
                                className={cn(fieldState.isDirty && DIRTY_RING)}
                              />
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-card/60 backdrop-blur-sm">
                    <CardHeader>
                      <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        Supplier
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="supplierName"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel>Name</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => field.onChange(e.target.value || null)}
                                  placeholder="Supplier name"
                                  className={cn(fieldState.isDirty && DIRTY_RING)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="supplierEmail"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel>Email</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => field.onChange(e.target.value || null)}
                                  placeholder="supplier@example.com"
                                  className={cn(fieldState.isDirty && DIRTY_RING)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="supplierPhone"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel>Phone</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => field.onChange(e.target.value || null)}
                                  placeholder="Phone number"
                                  className={cn(fieldState.isDirty && DIRTY_RING)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="supplierWebsite"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel>Website</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => field.onChange(e.target.value || null)}
                                  placeholder="https://example.com"
                                  className={cn(fieldState.isDirty && DIRTY_RING)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="supplierTaxId"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel>Tax ID</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => field.onChange(e.target.value || null)}
                                  placeholder="Tax ID"
                                  className={cn(fieldState.isDirty && DIRTY_RING)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="supplierRegistrationNumber"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel>Registration Number</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => field.onChange(e.target.value || null)}
                                  placeholder="Company reg. number"
                                  className={cn(fieldState.isDirty && DIRTY_RING)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="supplierAddress"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel>Address</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                value={field.value ?? ""}
                                onChange={(e) => field.onChange(e.target.value || null)}
                                placeholder="Supplier address"
                                className={cn(fieldState.isDirty && DIRTY_RING)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="deliveryAddress"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel>Delivery Address (ship-to)</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                value={field.value ?? ""}
                                onChange={(e) => field.onChange(e.target.value || null)}
                                placeholder="Ship-to address, if printed on the bill"
                                className={cn(fieldState.isDirty && DIRTY_RING)}
                              />
                            </FormControl>
                            <FormDescription>
                              Weighed against the supplier&apos;s address when matching the supplier.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </CardContent>
                  </Card>

                  <Card className="bg-card/60 backdrop-blur-sm">
                    <CardHeader>
                      <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        Amounts
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-4">
                        <FormField
                          control={form.control}
                          name="subTotal"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel>Subtotal</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.01"
                                  {...field}
                                  {...numberFieldProps(field)}
                                  className={cn(fieldState.isDirty && DIRTY_RING)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="taxAmount"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel>Tax Amount</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.01"
                                  {...field}
                                  {...numberFieldProps(field)}
                                  className={cn(fieldState.isDirty && DIRTY_RING)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="totalAmount"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel>Total Amount</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.01"
                                  {...field}
                                  {...numberFieldProps(field)}
                                  className={cn(fieldState.isDirty && DIRTY_RING)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-card/60 backdrop-blur-sm">
                    <CardHeader>
                      <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        Payment details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="paymentRef"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel>Payment Reference</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => field.onChange(e.target.value || null)}
                                  placeholder="Payment reference"
                                  className={cn(fieldState.isDirty && DIRTY_RING)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="sortCode"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel>Sort Code</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => field.onChange(e.target.value || null)}
                                  placeholder="e.g. 12-34-56"
                                  className={cn(fieldState.isDirty && DIRTY_RING)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="accountNumber"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel>Account Number</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => field.onChange(e.target.value || null)}
                                  placeholder="Account number"
                                  className={cn(fieldState.isDirty && DIRTY_RING)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="iban"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel>IBAN</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => field.onChange(e.target.value || null)}
                                  placeholder="e.g. GB29 NWBK 6016 1331 9268 19"
                                  className={cn(fieldState.isDirty && DIRTY_RING)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-card/60 backdrop-blur-sm">
                    <CardHeader className="flex-row items-center justify-between space-y-0">
                      <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        Line items
                      </CardTitle>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          append({
                            id: null,
                            description: "",
                            sku: null,
                            quantity: 1,
                            unit: null,
                            unitPrice: 0,
                            taxAmount: 0,
                            taxRate: 0,
                            sortOrder: fields.length,
                            category: null,
                            spendClass: null,
                            tags: [],
                          })
                        }
                      >
                        <Plus className="size-4" />
                        Add Item
                      </Button>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {fields.length === 0 && (
                        <EmptyState icon={ListPlus} title="No line items">
                          Add the lines from the bill so they can be coded and matched to a purchase order.
                        </EmptyState>
                      )}
                      {fields.map((field, index) => (
                        <LineItemRow
                          key={field._rhfId}
                          index={index}
                          control={form.control}
                          categories={categories}
                          suggestion={field.id ? suggestionsById.get(field.id) : undefined}
                          dragIndex={dragIndex}
                          lineTotal={lineTotals[index] ?? 0}
                          currency={currency}
                          onDragStart={handleDragStart}
                          onDragOver={handleDragOver}
                          onDragEnd={handleDragEnd}
                          onRemove={remove}
                        />
                      ))}
                      {fields.length > 0 && (
                        // The invariant made visible: lines that do not add up to the bill total are the
                        // commonest extraction slip, and it is cheaper to see here than after approval.
                        <div className="flex justify-end pt-1 text-sm">
                          <span className={cn("text-muted-foreground", linesMismatch && "text-destructive")}>
                            Lines total{" "}
                            <span className="font-semibold tabular-nums text-foreground">
                              {formatCurrency(linesTotal, currency)}
                            </span>{" "}
                            of {formatCurrency(watchedTotal, currency)}
                          </span>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {saveError && <p className="text-destructive text-sm">{saveError}</p>}

                  {/* Pinned to the bottom of the form panel: the bill is long, and the save should not be a
                      scroll away from the field just corrected. */}
                  <div className="sticky bottom-0 -mx-6 flex items-center justify-end gap-3 border-t bg-background/85 px-6 py-3 backdrop-blur-sm">
                    {form.formState.isDirty && (
                      <span className="mr-auto inline-flex items-center gap-1.5 text-xs text-amber-600">
                        <span className="size-1.5 rounded-full bg-current" /> Unsaved changes
                      </span>
                    )}
                    <Button type="button" variant="outline" onClick={goBack}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={saving || !form.formState.isDirty}>
                      {saving ? <Spinner className="size-4" /> : <Save className="size-4" />}
                      Save Changes
                    </Button>
                  </div>
                </form>
              </Form>

              <CreateSupplierDialog
                open={resources.newSupplierOpen}
                onOpenChange={resources.setNewSupplierOpen}
                onCreated={resources.refreshSuppliers}
              />
            </div>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}
